import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import {
  decayTable,
  fractionText,
  type HalfLifeResult,
  type HalfLifeSolve,
  secondsToUnit,
} from "@/lib/tool-kit/chem-reference/half-life";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";

export interface HalfLifeResultStrings {
  remainingLabel: string;
  initialLabel: string;
  timeLabel: string;
  halfLifeLabel: string;
  halfLivesElapsed: string;
  percentRemaining: string;
  decayConstant: string;
  meanLife: string;
  perUnit: string;
  working: string;
  workK: string;
  workKFromAmounts: string;
  workRemaining: string;
  workInitial: string;
  workTime: string;
  workHalfLife: string;
  workLambda: string;
  workTau: string;
  tableHeading: string;
  columnHalfLives: string;
  columnFraction: string;
  columnPercent: string;
  columnAmount: string;
  columnTime: string;
  copy: string;
  copied: string;
  copyText: string;
}

export interface Display {
  fmt: NumberFormatter;
  sig: number;
  /** Amount unit text, possibly empty. */
  amountUnit: string;
  timeUnit: string;
  halfUnit: string;
}

function withUnit(value: string, unit: string): string {
  return unit === "" ? value : `${value} ${unit}`;
}

/** The headline figure of the solved quantity, with its unit. */
export function solvedValue(
  solve: HalfLifeSolve,
  result: HalfLifeResult,
  d: Display
): string {
  const n = (v: number) => formatSig(d.fmt, v, d.sig);
  if (solve === "remaining") {
    return withUnit(n(result.remaining), d.amountUnit);
  }
  if (solve === "initial") {
    return withUnit(n(result.initial), d.amountUnit);
  }
  if (solve === "time") {
    return withUnit(n(secondsToUnit(result.timeSec, d.timeUnit)), d.timeUnit);
  }
  return withUnit(n(secondsToUnit(result.halfLifeSec, d.halfUnit)), d.halfUnit);
}

const SOLVE_LABEL_KEY = {
  remaining: "remainingLabel",
  initial: "initialLabel",
  time: "timeLabel",
  halfLife: "halfLifeLabel",
} as const;

function workingLines(
  solve: HalfLifeSolve,
  r: HalfLifeResult,
  d: Display,
  s: HalfLifeResultStrings
): string[] {
  const n = (v: number) => formatSig(d.fmt, v, d.sig);
  const vars = {
    n0: n(r.initial),
    n: n(r.remaining),
    time: withUnit(n(secondsToUnit(r.timeSec, d.timeUnit)), d.timeUnit),
    half: withUnit(n(secondsToUnit(r.halfLifeSec, d.halfUnit)), d.halfUnit),
    k: n(r.halfLives),
    factor: n(0.5 ** r.halfLives),
  };
  if (solve === "remaining") {
    return [t(s.workK, vars), t(s.workRemaining, vars)];
  }
  if (solve === "initial") {
    return [t(s.workK, vars), t(s.workInitial, vars)];
  }
  return [
    t(s.workKFromAmounts, vars),
    t(solve === "time" ? s.workTime : s.workHalfLife, vars),
  ];
}

export function HalfLifeResults({
  solve,
  result,
  display,
  strings,
}: {
  solve: HalfLifeSolve;
  result: HalfLifeResult;
  display: Display;
  strings: HalfLifeResultStrings;
}) {
  const { fmt, sig } = display;
  const n = (v: number) => formatSig(fmt, v, sig);
  const halfInUnit = secondsToUnit(result.halfLifeSec, display.halfUnit);
  const lambdaPerUnit = Math.LN2 / halfInUnit;
  const meanInUnit = halfInUnit / Math.LN2;
  const lines = workingLines(solve, result, display, strings);
  const solved = solvedValue(solve, result, display);
  const lambdaText = t(strings.perUnit, {
    value: n(lambdaPerUnit),
    unit: display.halfUnit,
  });
  const rows = decayTable(result.initial, result.halfLifeSec).map((row) => [
    String(row.halfLives),
    fractionText(row.halfLives),
    `${n(row.fraction * 100)}%`,
    withUnit(n(row.amount), display.amountUnit),
    withUnit(n(secondsToUnit(row.timeSec, display.halfUnit)), display.halfUnit),
  ]);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings[SOLVE_LABEL_KEY[solve]]}
          value={solved}
        />
        <StatCard
          label={strings.halfLivesElapsed}
          value={n(result.halfLives)}
        />
        <StatCard
          label={strings.percentRemaining}
          value={`${n(result.fraction * 100)}%`}
        />
        <StatCard label={strings.decayConstant} value={lambdaText} />
        <StatCard
          label={strings.meanLife}
          value={withUnit(n(meanInUnit), display.halfUnit)}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {t(strings.workLambda, {
            half: withUnit(n(halfInUnit), display.halfUnit),
            lambda: lambdaText,
          })}
        </p>
        <p>
          {t(strings.workTau, {
            lambda: lambdaText,
            tau: withUnit(n(meanInUnit), display.halfUnit),
          })}
        </p>
      </div>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
        <DataTable
          head={[
            strings.columnHalfLives,
            strings.columnFraction,
            strings.columnPercent,
            strings.columnAmount,
            strings.columnTime,
          ]}
          rows={rows}
        />
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          solved: `${strings[SOLVE_LABEL_KEY[solve]]}: ${solved}`,
          k: n(result.halfLives),
          percent: n(result.fraction * 100),
        })}
      />
    </>
  );
}
